import type { DatasetStructureChannel } from "@/api";

/**
 * 진단 그리드 채널과 데이터셋 구조 채널을 결합한다.
 *
 * **컬럼명 파싱을 여기서 하지 않는다.** namespace 분해와 그룹 분류의 SoT 는 hatchery-data
 * (`schemas/_channel.py`)다. 그 모듈 docstring 은 "소비자(registry 진단, FE)가 컬럼명
 * 역추론을 각자 재구현하지 않게 한 곳으로 모은다" 고 정한다. registry 도
 * `service/channel_structure.py` 에서 pass-through 만 한다. 그래서 FE 는 그 결과인
 * `GET /v1/datasets/{uuid}/structure` 응답을 읽는다.
 *
 * 결합 키는 `<topic>::<name>` 이다. 진단 위상 그리드가 쓰는 컬럼명과 같은 형식이다.
 * backend `worker/derived/diagnostic.py` 가 `assembled.columns` 키를 그대로 쓴다.
 */

export type ParquetChannelGroupId = "action" | "observation" | "other";

/**
 * 그룹 렌더 순서(observation → action → other).
 *
 * **Observation 이 먼저다.** Observation 은 로봇이 실제로 그렇게 움직였는지 잰 값이다.
 * 화면이 답해야 하는 질문("이 에피소드는 어땠나")에 먼저 답한다. 운동 보기의 기본 축과
 * 그리퍼 판정의 기본 기준 채널도 Observation 이다. 그래서 세 곳이 같은 쪽을 기본으로 둔다.
 * 명령(Action)은 사용자가 그 값을 읽은 다음 "무엇을 시켰길래" 를 확인하는 자리다.
 *
 * 이 배열 하나가 다음 다섯 표면의 순서를 정한다.
 * - 채널 밴드 카드·스파크라인 열
 * - 타이밍 표
 * - 운동 축 스위처
 * - 요약 통계 카드
 * - 구간 표
 */
export const GROUP_ORDER: readonly ParquetChannelGroupId[] = ["observation", "action", "other"];

// 한 채널(벡터)에서 펼칠 최대 dim 수. 비정상적으로 넓은 컬럼(임베딩 등)이 스파크라인을
// 수백 개 만들지 않게 막는다.
export const MAX_DIMS_PER_COLUMN = 32;

/** 진단 그리드 채널 키 → 구조 채널. */
export type ChannelStructureIndex = ReadonlyMap<string, DatasetStructureChannel>;

export function buildChannelIndex(
  channels: readonly DatasetStructureChannel[],
): ChannelStructureIndex {
  return new Map(
    channels.map((channel) => [`${channel.topic}::${channel.name}`, channel] as const),
  );
}

/**
 * 구조가 준 group 을 렌더 그룹으로 옮긴다.
 *
 * 어휘는 hatchery-data `CHANNEL_GROUP_*` 와 같은 값(`action`/`observation`/`other`)이다.
 * 그래서 그대로 쓴다. 구조에 없는 채널이나 모르는 값은 `other` 로 모아 정렬 끝에 둔다.
 * 여기서 **이름으로** 그룹을 추론하지 않는다. 이 파일은 그 중복을 없애려고 있다.
 *
 * 단, `group` 이 전부 비어 오는 응답이 있다. hatchery-data `2026.7.29` 로 적재된
 * 데이터셋은 structure 의 `dim`·`dtype`·`group` 이 전부 null 이다(`arm_side`·`media` 는 온다).
 * 그때는 topic 으로 그룹을 정한다. 이것은 이름 파싱이 아니라 `<topic>::<name>` **키 계약을
 * 읽는 것**이다. 그래서 위 금지에 해당하지 않는다. topic 은 서버가 정한 namespace 이고,
 * 컬럼명에서 추론한 값이 아니다.
 */
export function renderGroup(
  channel: DatasetStructureChannel | undefined,
  key: string,
): ParquetChannelGroupId {
  const group = channel?.group;
  if (group === "action" || group === "observation") {
    return group;
  }
  return topicGroup(channel?.topic ?? keyTopic(key));
}

/** 진단 그리드 키(`<topic>::<name>`)의 topic 부분. 구분자가 없으면 빈 문자열. */
function keyTopic(key: string): string {
  const index = key.indexOf("::");
  return index < 0 ? "" : key.slice(0, index);
}

// `observation_vector`·`observation_image` 를 한 그룹으로 합친다. `auxiliary_vector` 등 나머지는
// other 로 모은다.
function topicGroup(topic: string): ParquetChannelGroupId {
  if (topic === "action") {
    return "action";
  }
  return topic.startsWith("observation") ? "observation" : "other";
}

// 팔 방향 표기. `ee_pose_left`·`hand_right` 처럼 같은 채널이 팔마다 하나씩 있다. 그래서 카드가
// 둘씩 보이는 이유를 이름에서 바로 알 수 있게 한다.
const ARM_SIDE_LABEL: Record<"left" | "right", string> = {
  left: "Left",
  right: "Right",
};

/**
 * 카드 제목. 구조가 준 채널 이름에 팔 방향을 함께 적는다. 구조에 없으면 원본 키를 그대로 쓴다.
 *
 * 팔 방향은 **응답의 `arm_side` 로만 붙인다.** 이름 문자열에서 `_left` 를 추론하지 않는다.
 * 이 파일은 그 중복을 없애려고 있다. 방향이 없는 채널(단완·공용)은 이름만 표시한다.
 *
 * 이 금지에는 **표시 전용 예외가 하나** 있다.
 * - 운동 보기의 좌우 열 배치는 `motion/motionArms.ts` 의 `displayArmSide` 가 판정한다.
 * - 그 함수는 `arm_side` 가 null 이면 이름의 방향 토큰을 본다.
 * - 서버의 방향 도출은 접두와 접미만 본다. 그래서 `arm_left_ee_pose` 처럼 방향이 이름 중간에
 *   있는 채널을 놓친다(ZERG-643).
 * 그 판정은 열 배치에만 쓰고 제목에는 쓰지 않는다. 제목은 이 파일의 규칙을 따른다.
 *
 * 폴백 제목이 보기 나쁜 것은 의도다. 그리드에 있는 채널이 구조에 없으면 두 응답이 서로
 * 맞지 않는 것이다. 이름을 보기 좋게 지어내면 그 불일치가 화면에서 사라진다.
 */
export function channelTitle(key: string, channel: DatasetStructureChannel | undefined): string {
  if (channel === undefined) {
    return key;
  }
  return withArmSide(channel.name, channel);
}

/**
 * 벡터 채널의 차원 하나에 붙일 제목. 예: `ee_pose[2] (왼팔)`.
 *
 * 팔 표기는 항상 뒤에 둔다. `ee_pose (왼팔)[2]` 처럼 중간에 두면 인덱스가 팔에 속한 것처럼
 * 읽힌다.
 */
export function channelDimTitle(
  key: string,
  channel: DatasetStructureChannel | undefined,
  dim: number,
): string {
  if (channel === undefined) {
    return `${key}[${dim}]`;
  }
  return withArmSide(`${channel.name}[${dim}]`, channel);
}

function withArmSide(text: string, channel: DatasetStructureChannel): string {
  const side = channel.arm_side === null ? null : ARM_SIDE_LABEL[channel.arm_side];
  return side === null ? text : `${text} (${side})`;
}
